import React, { useState, useEffect, useMemo } from 'react';
import propTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { fetchReportsDashboard, fetchProperties, fetchPropertyGroups } from '../../../misc/apiRequests';
import Swal from 'sweetalert2';
import '../../../../assets/css/reports.css';

const PERIODS = ['day', 'week', 'month', 'year'];

// The listing picker holds one string so a property and a group can share a
// <select>. The API rejects being sent both ids at once, so the prefix decides
// which one goes on the request.
const PROPERTY_PREFIX = 'p-';
const GROUP_PREFIX = 'g-';

const filterFromValue = (value) => {
  if (value.startsWith(PROPERTY_PREFIX)) return { property_id: value.slice(PROPERTY_PREFIX.length) };
  if (value.startsWith(GROUP_PREFIX)) return { property_group_id: value.slice(GROUP_PREFIX.length) };

  return {};
};

// The API buckets by hour for a day, by month for a year, by day otherwise
const bucketStep = period => (period === 'day' ? 'hour' : period === 'year' ? 'month' : 'day');

// Buckets with zero activity are omitted by the API. Without filling them the
// x-axis silently skips empty days and the trend reads as denser than it was.
const fillSeries = (series = [], period, startsAt, endsAt) => {
  if (!startsAt || !endsAt) return [];

  const step = bucketStep(period);
  const counts = new Map(series.map(p => [new Date(p.bucket).getTime(), p.count]));

  // Bucket boundaries come back UTC-aligned, so walk them in UTC or the keys won't match
  const cursor = new Date(startsAt);
  if (step === 'hour') cursor.setUTCMinutes(0, 0, 0);
  else if (step === 'day') cursor.setUTCHours(0, 0, 0, 0);
  else { cursor.setUTCDate(1); cursor.setUTCHours(0, 0, 0, 0); }

  const end = new Date(endsAt).getTime();
  const filled = [];
  // Guards against a malformed range spinning this loop forever
  while (cursor.getTime() <= end && filled.length < 400) {
    filled.push({ bucket: new Date(cursor), count: counts.get(cursor.getTime()) || 0 });
    if (step === 'hour') cursor.setUTCHours(cursor.getUTCHours() + 1);
    else if (step === 'day') cursor.setUTCDate(cursor.getUTCDate() + 1);
    else cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }

  return filled;
};

const formatBucket = (date, period) => {
  const opts = period === 'day' ? { hour: 'numeric' }
    : period === 'year' ? { month: 'short' }
      : { month: 'short', day: 'numeric' };

  return date.toLocaleString(undefined, { ...opts, timeZone: 'UTC' });
};

// Round the axis top up to a clean number and hand back its tick values
const niceScale = max => {
  if (!max || max <= 0) return { top: 4, ticks: [0, 1, 2, 3, 4] };

  const rough = max / 4;
  const pow = Math.pow(10, Math.floor(Math.log10(rough)));
  const stepSize = [1, 2, 2.5, 5, 10].map(m => m * pow).find(c => c >= rough) || 10 * pow;
  const top = Math.ceil(max / stepSize) * stepSize;

  const ticks = [];
  for (let v = 0; v <= top + 1e-9; v += stepSize) ticks.push(Math.round(v * 100) / 100);

  return { top, ticks };
};

const compact = value => {
  const numb = Number(value) || 0;
  if (numb >= 1000000) return `${(numb / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
  if (numb >= 10000) return `${(numb / 1000).toFixed(1).replace(/\.0$/, '')}K`;

  return numb.toLocaleString();
};

const StatTile = ({ label, value, note }) => (
  <div className='viz-tile'>
    <span className='viz-tile-label'>{label}</span>
    <span className='viz-tile-value'>{compact(value)}</span>
    {note && <span className='viz-tile-note'>{note}</span>}
  </div>
);

// Single-series trend. One series means no legend box — the card title names it.
const TrendChart = ({ points, period, label }) => {
  const [hoverIndex, setHoverIndex] = useState(null);

  const width = 720;
  const height = 220;
  const padding = { top: 16, right: 16, bottom: 30, left: 46 };
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;

  if (!points.length) return <p className='viz-empty'>No activity recorded in this period yet.</p>;

  const { top, ticks } = niceScale(Math.max(...points.map(p => p.count)));
  const xAt = i => (points.length === 1 ? plotWidth / 2 : (i / (points.length - 1)) * plotWidth);
  const yAt = value => plotHeight - (value / top) * plotHeight;

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i).toFixed(2)} ${yAt(p.count).toFixed(2)}`)
    .join(' ');
  const areaPath = `${linePath} L ${xAt(points.length - 1).toFixed(2)} ${plotHeight} L ${xAt(0).toFixed(2)} ${plotHeight} Z`;

  // Cap x labels so they never collide, and always keep the last bucket
  const labelStride = Math.max(1, Math.ceil(points.length / 7));
  const peakIndex = points.reduce((best, p, i) => (p.count > points[best].count ? i : best), 0);

  const handleMove = ev => {
    const rect = ev.currentTarget.getBoundingClientRect();
    const svgX = ((ev.clientX - rect.left) / rect.width) * width;
    const ratio = (svgX - padding.left) / plotWidth;
    const index = Math.round(ratio * (points.length - 1));
    setHoverIndex(Math.min(points.length - 1, Math.max(0, index)));
  };

  const active = hoverIndex === null ? null : points[hoverIndex];

  return (
    <div className='viz-plot-wrap'>
      <svg
        className='viz-plot'
        viewBox={`0 0 ${width} ${height}`}
        role='img'
        aria-label={`${label} over the selected period`}
      >
        <g transform={`translate(${padding.left} ${padding.top})`}>
          {ticks.map(tick => (
            <g key={tick}>
              <line
                x1={0} x2={plotWidth} y1={yAt(tick)} y2={yAt(tick)}
                stroke={tick === 0 ? 'var(--baseline)' : 'var(--gridline)'} strokeWidth={1}
              />
              <text className='viz-axis-text' x={-10} y={yAt(tick)} dy='0.32em' textAnchor='end'>
                {compact(tick)}
              </text>
            </g>
          ))}

          <path d={areaPath} fill='var(--series-1)' fillOpacity={0.1} />
          <path
            d={linePath}
            fill='none'
            stroke='var(--series-1)'
            strokeWidth={2}
            strokeLinejoin='round'
            strokeLinecap='round'
          />

          {points.map((p, i) => (
            (i % labelStride === 0 || i === points.length - 1) && (
              <text
                key={p.bucket.getTime()}
                className='viz-axis-text'
                x={xAt(i)}
                y={plotHeight + 18}
                textAnchor={i === points.length - 1 ? 'end' : i === 0 ? 'start' : 'middle'}
              >
                {formatBucket(p.bucket, period)}
              </text>
            )
          ))}

          {/* Direct-label the peak only — a value on every point goes unread.
              The peak often sits exactly on the axis top, so drop the label below
              the point when there is no headroom for it rather than clipping it. */}
          {points[peakIndex].count > 0 && (
            <text
              className='viz-axis-text'
              style={{ fill: 'var(--text-secondary)', fontWeight: 600 }}
              x={Math.min(Math.max(xAt(peakIndex), 14), plotWidth - 14)}
              y={yAt(points[peakIndex].count) < 14
                ? yAt(points[peakIndex].count) + 18
                : yAt(points[peakIndex].count) - 10}
              textAnchor='middle'
            >
              {compact(points[peakIndex].count)}
            </text>
          )}

          {active && (
            <g>
              <line
                x1={xAt(hoverIndex)} x2={xAt(hoverIndex)} y1={0} y2={plotHeight}
                stroke='var(--baseline)' strokeWidth={1}
              />
              <circle
                cx={xAt(hoverIndex)} cy={yAt(active.count)} r={5}
                fill='var(--series-1)' stroke='var(--surface-1)' strokeWidth={2}
              />
            </g>
          )}

          <rect
            className='viz-hit'
            x={0} y={0} width={plotWidth} height={plotHeight}
            onMouseMove={handleMove}
            onMouseLeave={() => setHoverIndex(null)}
          />
        </g>
      </svg>

      {active && (
        <div
          className='viz-tooltip'
          style={{
            left: `${((padding.left + xAt(hoverIndex)) / width) * 100}%`,
            top: `${((padding.top + yAt(active.count) - 12) / height) * 100}%`
          }}
        >
          <span className='viz-tooltip-label'>{formatBucket(active.bucket, period)}</span>
          <span className='viz-tooltip-value'>{active.count.toLocaleString()} {label.toLowerCase()}</span>
        </div>
      )}
    </div>
  );
};

// Nominal categories all take slot 1 — bar length already encodes the value
const BarRows = ({ rows, emptyText }) => {
  if (!rows.length) return <p className='viz-empty'>{emptyText}</p>;

  const max = Math.max(...rows.map(r => r.value), 1);

  return (
    <div>
      {rows.map(row => (
        <div className='viz-bar-row' key={row.name}>
          <span className='viz-bar-name' title={row.name}>{row.name}</span>
          <div className='viz-bar-track'>
            <div className='viz-bar-fill' style={{ width: `${(row.value / max) * 100}%` }} />
          </div>
          <span className='viz-bar-value'>{row.value.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
};

const PropertyReports = ({ handleLoader }) => {
  const { t } = useTranslation();

  const [period, setPeriod] = useState('week');
  const [dashboard, setDashboard] = useState(null);
  const [showTrafficTable, setShowTrafficTable] = useState(false);
  const [listingValue, setListingValue] = useState('');
  const [ownedProperties, setOwnedProperties] = useState([]);
  const [ownedGroups, setOwnedGroups] = useState([]);

  const traffic = dashboard?.traffic?.[period];
  const bookings = dashboard?.bookings?.[period];

  const trafficPoints = useMemo(
    () => fillSeries(traffic?.series, period, traffic?.starts_at, traffic?.ends_at),
    [traffic, period]
  );

  const statusRows = useMemo(() => (
    Object.entries(bookings?.by_status || {})
      .map(([name, value]) => ({ name: name.replace(/^./, c => c.toUpperCase()), value }))
      .sort((a, b) => b.value - a.value)
  ), [bookings]);

  const typeRows = useMemo(() => (
    Object.entries(bookings?.by_property_type || {})
      .map(([name, value]) => ({ name: name.replace(/\b\w/g, c => c.toUpperCase()), value }))
      .sort((a, b) => b.value - a.value)
  ), [bookings]);

  const listingRows = useMemo(() => (dashboard?.top_listings || []).map(listing => ({
    name: [listing.property_name, listing.property_unit, listing.property_type].filter(Boolean).join(' · ') || `Listing #${listing.property_id}`,
    value: listing.views
  })), [dashboard]);

  useEffect(() => {
    handleLoader(true);
    fetchReportsDashboard(filterFromValue(listingValue))
      .then(response => {
        if (response.success) setDashboard(response);
        if (!response.success) {
          Swal.fire({
            position: 'top-end',
            toast: true,
            timerProgressBar: true,
            icon: 'error',
            title: Array.isArray(response.errors) ? response.errors.join("\n") : response.errors,
            showConfirmButton: false,
            timer: 3000
          })
        }
        handleLoader(false);
      });
  }, [handleLoader, listingValue]);

  // Populates the listing picker. Both endpoints already scope to the caller's
  // own records, so there is nothing here the caller can't filter to.
  useEffect(() => {
    fetchProperties(100, 1)
      .then(response => { if (response.success) setOwnedProperties(response.records || []); });

    fetchPropertyGroups(100, 1)
      .then(response => { if (response.success) setOwnedGroups(response.records || []); });
  }, []);

  const periodLabel = { day: t('f_today'), week: t('f_thisWeek'), month: t('f_thisMonth'), year: t('f_thisYear') };

  const propertyLabel = property => (
    [property.property_name || property.property_unit || property.street_address, property.settlement]
      .filter(Boolean).join(' · ') || `#${property.id}`
  );

  // The API echoes back the filter it actually applied, so show that rather than
  // the local selection - they diverge if a request is rejected.
  const appliedFilter = dashboard?.filter;
  const appliedFilterLabel = appliedFilter
    ? (appliedFilter.group_name || appliedFilter.property_name || appliedFilter.property_unit || `#${appliedFilter.id}`)
    : null;

  return (
    <div className='acct-menu-container viz-root'>
      <h1>{t('f_reports')}</h1>
      <p className='viz-card-sub'>
        {dashboard?.scope === 'site' ? t('f_reportsScopeSite') : t('f_reportsScopeOwn')}
        {appliedFilterLabel && ` — ${t('f_showingFor')} ${appliedFilterLabel}`}
      </p>

      {/* One filter row above everything it scopes */}
      <div className='viz-filters' role='group' aria-label={t('f_reportsPeriod')}>
        {PERIODS.map(key => (
          <button
            type='button'
            key={key}
            className='viz-filter-btn'
            aria-pressed={period === key}
            onClick={() => setPeriod(key)}
          >
            {periodLabel[key]}
          </button>
        ))}

        {(ownedProperties.length > 0 || ownedGroups.length > 0) && (
          <select
            className='viz-filter-select'
            aria-label={t('f_reportsListing')}
            value={listingValue}
            onChange={e => setListingValue(e.target.value)}
          >
            <option value=''>{t('f_allListings')}</option>
            {ownedGroups.length > 0 && (
              <optgroup label={t('g_propertyComplexes')}>
                {ownedGroups.map(group => (
                  <option key={`g${group.id}`} value={`${GROUP_PREFIX}${group.id}`}>{group.group_name}</option>
                ))}
              </optgroup>
            )}
            {ownedProperties.length > 0 && (
              <optgroup label={t('g_rentalProperties')}>
                {ownedProperties.map(property => (
                  <option key={`p${property.id}`} value={`${PROPERTY_PREFIX}${property.id}`}>{propertyLabel(property)}</option>
                ))}
              </optgroup>
            )}
          </select>
        )}
      </div>

      <div className='viz-kpi-row'>
        <StatTile label={t('f_totalViews')} value={traffic?.total_views || 0} note={periodLabel[period]} />
        <StatTile label={t('f_uniqueVisitors')} value={traffic?.unique_visitors || 0} note={periodLabel[period]} />
        <StatTile label={t('f_totalBookings')} value={bookings?.total || 0} note={periodLabel[period]} />
      </div>

      <div className='viz-card'>
        <div className='viz-card-head'>
          <h3 className='viz-card-title'>{t('f_trafficOverTime')}</h3>
          <button
            type='button'
            className='viz-table-toggle'
            onClick={() => setShowTrafficTable(!showTrafficTable)}
          >
            {showTrafficTable ? t('f_hideTable') : t('f_viewAsTable')}
          </button>
        </div>
        <p className='viz-card-sub'>{t('f_pageViews')} — {periodLabel[period]}</p>

        <TrendChart points={trafficPoints} period={period} label={t('f_pageViews')} />

        {showTrafficTable && (
          <div className='viz-table-scroll'>
            <table className='viz-table'>
              <thead>
                <tr>
                  <th scope='col'>{t('f_period')}</th>
                  <th scope='col' className='viz-num'>{t('f_pageViews')}</th>
                </tr>
              </thead>
              <tbody>
                {trafficPoints.map(point => (
                  <tr key={point.bucket.getTime()}>
                    <td>{formatBucket(point.bucket, period)}</td>
                    <td className='viz-num'>{point.count.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className='viz-card'>
        <h3 className='viz-card-title'>{t('f_bookings')}</h3>
        <p className='viz-card-sub'>{t('f_bookingsByStatus')} — {periodLabel[period]}</p>
        <BarRows rows={statusRows} emptyText={t('f_noBookings')} />

        {typeRows.length > 0 && (
          <>
            <p className='viz-card-sub' style={{ marginTop: '18px' }}>{t('f_bookingsByType')}</p>
            <BarRows rows={typeRows} emptyText={t('f_noBookings')} />
          </>
        )}
      </div>

      <div className='viz-card'>
        <h3 className='viz-card-title'>{t('f_topListings')}</h3>
        <p className='viz-card-sub'>{t('f_topListingsSub')}</p>
        <BarRows rows={listingRows} emptyText={t('f_noListingViews')} />
      </div>
    </div>
  );
};

PropertyReports.propTypes = {
  handleLoader: propTypes.func.isRequired
};

export default PropertyReports;
